import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Locale, ProjectDetailDto, ProjectSummaryDto, SiteBundleDto } from '@portfolio/shared';
import { api } from '@/lib/api';
import { useLocale } from './i18n';

const siteQuery = (locale: Locale) => ({
  queryKey: ['public', 'site', locale],
  queryFn: () => api.get<SiteBundleDto>('/site', { lang: locale }),
  staleTime: 60_000,
});

/** Public content in the page's language — each language is cached separately. */
export const useSite = () => useQuery(siteQuery(useLocale().locale));

/** Loads the other language's content ahead of a language switch, so it is instant. */
export function usePrefetchSite() {
  const qc = useQueryClient();
  return (locale: Locale) => void qc.prefetchQuery(siteQuery(locale));
}

export const useProjects = (
  filters: { category?: string; featured?: boolean; home?: boolean; limit?: number } = {},
) => {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['public', 'projects', locale, filters],
    queryFn: () =>
      api.get<ProjectSummaryDto[]>('/projects', {
        category: filters.category,
        featured: filters.featured === undefined ? undefined : String(filters.featured),
        home: filters.home ? 'true' : undefined,
        limit: filters.limit,
        lang: locale,
      }),
    staleTime: 60_000,
  });
};

export const useProject = (slug: string) => {
  const { locale } = useLocale();
  return useQuery({
    queryKey: ['public', 'project', locale, slug],
    queryFn: () =>
      api.get<{ project: ProjectDetailDto; more: ProjectSummaryDto[] }>(
        `/projects/${encodeURIComponent(slug)}`,
        { lang: locale },
      ),
    retry: (count, err) => (err as { status?: number }).status !== 404 && count < 2,
  });
};

/**
 * Pins a section while the page scrolls: `outer` is a tall wrapper, `inner` the sticky content.
 * While pinned, `onProgress` receives 0→1 over `distance(viewportHeight)` px of scrolling, then
 * the page moves on. `scrollTo(p)` scrolls the page to a point of that stretch.
 *
 * The content sticks under the fixed navbar (`align: 'top'`) or centred in the space below it
 * (`'center'`). When it is taller than that space it sticks with its bottom on the viewport's,
 * so its end stays visible.
 */
export function useScrollPin<T extends HTMLElement = HTMLElement>({
  enabled,
  distance,
  align = 'top',
  onProgress,
}: {
  enabled: boolean;
  distance: (viewportHeight: number) => number;
  align?: 'top' | 'center';
  onProgress: (progress: number) => void;
}) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<T>(null);
  const [layout, setLayout] = useState({ top: 0, height: 0, distance: 0 });
  // Callers pass inline functions; keep the latest without re-subscribing every render.
  const latest = useRef({ distance, onProgress });
  latest.current = { distance, onProgress };

  useLayoutEffect(() => {
    if (!enabled) return;
    const el = inner.current;
    if (!el) return;
    const measure = () => {
      const nav = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
      const vh = window.innerHeight;
      const height = el.offsetHeight;
      const space = vh - nav;
      const top =
        height > space ? vh - height : align === 'center' ? nav + (space - height) / 2 : nav;
      setLayout({ top, height, distance: latest.current.distance(vh) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, align]);

  useEffect(() => {
    if (!enabled || !layout.distance) return;
    const onScroll = () => {
      const el = outer.current;
      if (!el) return;
      const p = (layout.top - el.getBoundingClientRect().top) / layout.distance;
      latest.current.onProgress(Math.min(1, Math.max(0, p)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled, layout.distance, layout.top]);

  const scrollTo = useCallback(
    (p: number) => {
      const el = outer.current;
      if (!enabled || !el || !layout.distance) return;
      const start = el.getBoundingClientRect().top + window.scrollY - layout.top;
      window.scrollTo({ top: start + p * layout.distance, behavior: 'smooth' });
    },
    [enabled, layout.distance, layout.top],
  );

  return {
    outer,
    inner,
    scrollTo,
    outerStyle: enabled && layout.height ? { height: layout.height + layout.distance } : undefined,
    innerStyle: enabled ? { top: layout.top } : undefined,
  };
}
